import { ref, shallowRef, type Ref, type ShallowRef } from 'vue'
import * as THREE from 'three'
import type { ImportedModelResult } from '@puppycad/cad-core'
import { logger } from '@/utils'

export interface ComponentPlacementSnapInput {
  /** 当前指针事件。 */
  event: PointerEvent
  /** 原有视平面求交得到的候选放置点。 */
  fallbackPosition: THREE.Vector3
  /** 当前插入预览根对象。 */
  previewObject: THREE.Object3D
}

export interface UseComponentPlacementOptions {
  /** 获取当前编辑器场景。 */
  getScene: () => THREE.Scene | null | undefined
  /** 获取当前编辑器相机。 */
  getCamera: () => THREE.Camera | null | undefined
  /** 获取承载视口事件的 DOM 元素。 */
  getDomElement: () => HTMLElement | null | undefined
  /** 通过 Viewer 统一挂载对象，避免绕开场景树维护逻辑。 */
  addObject: (object: THREE.Object3D, parent?: THREE.Object3D, index?: number) => THREE.Object3D | null | undefined
  /**
   * 解析插入预览阶段的吸附坐标。
   *
   * 说明：该回调只影响当前放置对象的临时位置，确认后仍由外层业务负责提交装配草稿或 reparent。
   */
  resolvePlacementSnap?: (input: ComponentPlacementSnapInput) => THREE.Vector3 | null | undefined
  /** 清理插入预览阶段的吸附视觉状态。 */
  clearPlacementSnap?: () => void
  /** 请求视口重绘。 */
  requestRender?: () => void
  /** 确认放置时回传最终世界坐标。 */
  onConfirm: (position: THREE.Vector3) => void
  /** 取消放置时通知外层业务清理状态。 */
  onCancel?: () => void
  /**
   * 预览阶段首次拿到有效放置位置后触发。
   * 用于按当前预览对象包围盒做一次性相机适配，避免等到最终点击确认后才缩放。
   */
  onFirstPreviewPositionResolved?: (targetObject: THREE.Object3D) => void
}

const PREVIEW_GROUP_NAME = 'inserted-component-preview'

/**
 * 组件放置锚点模式。
 *
 * 设计说明：
 * 普通导入模型沿用对象原点放置，装配体引用复制需要按视觉包围盒中心放置，
 * 因为引用复制对象的运行时原点可能来自源组件层级，和用户看到的实体中心并不一致。
 */
export enum ComponentPlacementAnchorMode {
  /** 使用 Object3D 原点作为鼠标跟随和确认放置锚点。 */
  ObjectOrigin = 'object-origin',
  /** 使用 Object3D 可见包围盒中心作为鼠标跟随和确认放置锚点。 */
  BoundingBoxCenter = 'bounding-box-center',
}

/**
 * 启动组件放置流程的可选参数。
 */
export interface ComponentPlacementStartOptions {
  /**
   * 放置锚点模式。
   *
   * @default ComponentPlacementAnchorMode.ObjectOrigin
   */
  anchorMode?: ComponentPlacementAnchorMode
  /**
   * 首次解析出有效预览位置后是否触发相机适配。
   *
   * @default true
   */
  focusOnFirstPreviewPosition?: boolean
  /**
   * 启动放置时是否立即同步到最近一次视口鼠标位置。
   *
   * @default false
   */
  syncToLastPointerPositionOnStart?: boolean
  /**
   * 是否启用吸附解析。
   *
   * @default true
   */
  enableSnap?: boolean
}

interface ExistingObjectPlacementState {
  /** 进入放置流程前已经挂在场景中的对象。 */
  readonly object: THREE.Object3D
  /** 原父级用于取消时确保对象仍回到同一局部坐标系。 */
  readonly parent: THREE.Object3D | null
  /** 进入放置流程前的局部位置。 */
  readonly position: THREE.Vector3
  /** 进入放置流程前的局部旋转。 */
  readonly quaternion: THREE.Quaternion
  /** 进入放置流程前的局部缩放。 */
  readonly scale: THREE.Vector3
}

interface PlacementTeardownOptions {
  /** 是否恢复已加载对象进入放置流程前的位姿。 */
  readonly restoreExistingObject?: boolean
}

/**
 * 从导入模型结果中解析可挂载的根对象。
 *
 * @param result 导入模型结果。
 * @returns 可放置的 Three.js 对象。
 */
export function resolveSceneObject(result: ImportedModelResult): THREE.Object3D {
  if (result.kind === 'composed-assembly') {
    return result.root
  }
  return result.object
}

/**
 * 创建组件放置工作流。
 *
 * @param options 放置流程所需的 Viewer 与业务回调。
 * @returns 组件放置控制器。
 */
export function useComponentPlacement(options: UseComponentPlacementOptions) {
  const isPlacing: Ref<boolean> = ref(false)
  // Three.js 对象禁止进入 Vue 深响应式；这里用 shallowRef 只追踪引用变化。
  const previewGroup: ShallowRef<THREE.Group | null> = shallowRef(null)

  const raycaster = new THREE.Raycaster()
  const ndc = new THREE.Vector2()
  const plane = new THREE.Plane()
  const cameraDirection = new THREE.Vector3()
  const intersection = new THREE.Vector3()
  const origin = new THREE.Vector3(0, 0, 0)
  const localPlacementPosition = new THREE.Vector3()
  const confirmedWorldPosition = new THREE.Vector3()
  const previewBounds = new THREE.Box3()
  const previewBoundsCenter = new THREE.Vector3()
  const previewAnchorPointAtGroupOrigin = new THREE.Vector3()
  const confirmPosition = new THREE.Vector3()
  const lastPointerClientPosition = new THREE.Vector2()

  let sceneObjectRef: THREE.Object3D | null = null
  let existingObjectPlacementState: ExistingObjectPlacementState | null = null
  let currentDomElement: HTMLElement | null = null
  let hasFocusedPreviewBounds = false
  let hasLockedPlacementPlane = false
  let hasReportedSnapResolveFailure = false
  let currentAnchorMode = ComponentPlacementAnchorMode.ObjectOrigin
  let shouldFocusOnFirstPreviewPosition = true
  let shouldResolvePlacementSnap = true
  let hasLastPointerClientPosition = false
  const selectionSkipSnapshot = new WeakMap<THREE.Object3D, unknown>()

  /**
   * 标记预览对象不参与选择。
   *
   * @param root 需要标记的对象根节点。
   */
  function markPreviewObjectSkipSelection(root: THREE.Object3D): void {
    root.traverse((object) => {
      selectionSkipSnapshot.set(object, object.userData?.skipSelection)
      object.userData.skipSelection = true
    })
  }

  /**
   * 恢复预览对象进入放置前的选择标记。
   *
   * @param root 需要恢复的对象根节点。
   */
  function restorePreviewObjectSkipSelection(root: THREE.Object3D): void {
    root.traverse((object) => {
      const previousValue = selectionSkipSnapshot.get(object)
      if (previousValue === undefined) {
        delete object.userData.skipSelection
      } else {
        object.userData.skipSelection = previousValue
      }
      selectionSkipSnapshot.delete(object)
    })
  }

  /**
   * 记录最近一次视口鼠标位置。
   *
   * @param event 鼠标或指针事件。
   */
  function rememberPointerPosition(event: Pick<MouseEvent, 'clientX' | 'clientY'>): void {
    lastPointerClientPosition.set(event.clientX, event.clientY)
    hasLastPointerClientPosition = true
  }

  /**
   * 将客户端坐标转换为 NDC 坐标。
   *
   * @param clientX 客户端 X 坐标。
   * @param clientY 客户端 Y 坐标。
   * @returns 转换成功时返回 true。
   */
  function updateNdcFromClientPosition(clientX: number, clientY: number): boolean {
    const dom = currentDomElement
    if (!dom) return false
    const rect = dom.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return false
    ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1
    ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1
    return true
  }

  /**
   * 判断坐标是否可用于放置。
   *
   * @param position 待检查坐标。
   * @returns 坐标有限时返回 true。
   */
  function isFinitePosition(position: THREE.Vector3 | null | undefined): position is THREE.Vector3 {
    return Boolean(position)
      && Number.isFinite(position?.x)
      && Number.isFinite(position?.y)
      && Number.isFinite(position?.z)
  }

  /**
   * 安全解析吸附坐标。
   *
   * @param event 当前指针事件。
   * @param fallbackPosition 视平面求交得到的候选坐标。
   * @param previewObject 当前放置对象。
   * @returns 有效吸附坐标；没有吸附或解析失败时返回 null。
   */
  function resolveSafeSnapPosition(
    event: PointerEvent | undefined,
    fallbackPosition: THREE.Vector3,
    previewObject: THREE.Object3D,
  ): THREE.Vector3 | null {
    if (!event || !shouldResolvePlacementSnap || !options.resolvePlacementSnap) {
      return null
    }

    try {
      const snapPosition = options.resolvePlacementSnap({
        event,
        fallbackPosition,
        previewObject,
      })
      return isFinitePosition(snapPosition) ? snapPosition : null
    } catch (error) {
      if (!hasReportedSnapResolveFailure) {
        logger.warn('[useComponentPlacement] placement snap resolver failed', {
          error,
          clientX: event.clientX,
          clientY: event.clientY,
        })
        hasReportedSnapResolveFailure = true
      }
      return null
    }
  }

  /**
   * 读取当前正在被鼠标驱动的对象。
   *
   * 设计说明：旧导入和引用复制使用临时 preview group；新插入接口已经把对象加载进场景，
   * 因此需要直接驱动该对象，但吸附、事件拦截和确认流程保持同一套。
   *
   * @returns 当前放置对象；没有有效放置流程时返回 null。
   */
  function resolvePlacementTargetObject(): THREE.Object3D | null {
    return previewGroup.value ?? existingObjectPlacementState?.object ?? null
  }

  /**
   * 把世界坐标应用到当前放置对象。
   *
   * @param targetObject 当前被放置的对象。
   * @param worldPosition 吸附或视平面求交得到的世界坐标。
   */
  function applyWorldPlacementPosition(
    targetObject: THREE.Object3D,
    worldPosition: THREE.Vector3,
  ): void {
    if (targetObject === previewGroup.value) {
      // preview group 使用锚点抵消，确保包围盒中心模式下用户看到的实体中心贴住鼠标。
      targetObject.position.copy(worldPosition).sub(previewAnchorPointAtGroupOrigin)
      targetObject.updateMatrixWorld(true)
      return
    }

    localPlacementPosition.copy(worldPosition)
    if (targetObject.parent) {
      targetObject.parent.updateMatrixWorld(true)
      targetObject.parent.worldToLocal(localPlacementPosition)
    }
    targetObject.position.copy(localPlacementPosition)
    targetObject.updateMatrixWorld(true)
  }

  /**
   * 获取确认放置时需要交给外层业务的世界坐标。
   *
   * @param targetObject 当前被放置的对象。
   * @returns 独立的世界坐标副本，避免外层持有内部复用向量。
   */
  function resolveConfirmWorldPosition(targetObject: THREE.Object3D): THREE.Vector3 {
    if (targetObject === previewGroup.value) {
      if (currentAnchorMode === ComponentPlacementAnchorMode.BoundingBoxCenter && sceneObjectRef) {
        return confirmPosition.copy(targetObject.position).add(sceneObjectRef.position).clone()
      }
      return targetObject.position.clone()
    }

    targetObject.getWorldPosition(confirmedWorldPosition)
    return confirmedWorldPosition.clone()
  }

  /**
   * 根据客户端坐标更新当前放置对象位置。
   *
   * @param clientX 客户端 X 坐标。
   * @param clientY 客户端 Y 坐标。
   * @param event 当前指针事件；没有事件时仅执行视平面求交，不执行吸附。
   */
  function updatePlacementPositionFromClientPosition(
    clientX: number,
    clientY: number,
    event?: PointerEvent,
  ): void {
    const camera = options.getCamera()
    const targetObject = resolvePlacementTargetObject()
    if (!camera || !targetObject) return
    if (!updateNdcFromClientPosition(clientX, clientY)) return

    raycaster.setFromCamera(ndc, camera)
    if (!hasLockedPlacementPlane) {
      camera.getWorldDirection(cameraDirection)
      // 首次命中前使用旧行为：用当前视线法向 + 全局原点构造临时平面，
      // 仅用于求出第一帧有效预览位置。
      plane.setFromNormalAndCoplanarPoint(cameraDirection, origin)
    }

    const hit = raycaster.ray.intersectPlane(plane, intersection)
    if (!hit) return
    if (!hasLockedPlacementPlane) {
      camera.getWorldDirection(cameraDirection)
      // 一旦拿到首个有效预览点，就把放置平面固定在“当前视线方向 + 首个命中点”上，
      // 避免预览阶段触发相机缩放后，后续 pointermove 因相机方向变化导致预览点跳变。
      plane.setFromNormalAndCoplanarPoint(cameraDirection, intersection)
      hasLockedPlacementPlane = true
    }

    const snapPosition = resolveSafeSnapPosition(event, intersection, targetObject)
    if (event && !snapPosition) {
      options.clearPlacementSnap?.()
    }
    applyWorldPlacementPosition(targetObject, snapPosition ?? intersection)
    options.requestRender?.()
    if (!hasFocusedPreviewBounds) {
      targetObject.updateMatrixWorld(true)
      if (shouldFocusOnFirstPreviewPosition) {
        options.onFirstPreviewPositionResolved?.(targetObject)
      }
      hasFocusedPreviewBounds = true
    }
  }

  /**
   * 根据指针事件更新当前放置对象位置。
   *
   * @param event 指针事件。
   */
  function updatePreviewPosition(event: PointerEvent): void {
    // 放置中的事件也继续刷新缓存，保证后续重新启动复制时能拿到最近位置。
    rememberPointerPosition(event)
    updatePlacementPositionFromClientPosition(event.clientX, event.clientY, event)
  }

  /**
   * 处理放置过程中的指针移动。
   *
   * @param event 指针事件。
   */
  function handlePointerMove(event: PointerEvent): void {
    if (!isPlacing.value) return
    event.stopPropagation()
    updatePreviewPosition(event)
  }

  /**
   * 处理放置确认点击。
   *
   * @param event 指针事件。
   */
  function handlePointerDown(event: PointerEvent): void {
    if (!isPlacing.value) return
    if (event.button !== 0) {
      event.stopPropagation()
      return
    }
    event.stopPropagation()
    event.preventDefault()
    // 点击确认前再按当前事件刷新一次，确保没有 pointermove 的情况下也按点击位置落点。
    updatePreviewPosition(event)
    const targetObject = resolvePlacementTargetObject()
    if (!targetObject) return
    options.onConfirm(resolveConfirmWorldPosition(targetObject))
  }

  /**
   * 吞掉放置期间的 click，避免落点确认后事件继续触发场景选择。
   *
   * @param event 鼠标事件。
   */
  function handleClick(event: MouseEvent): void {
    if (!isPlacing.value) return
    event.stopPropagation()
    event.preventDefault()
  }

  /**
   * 处理右键取消放置。
   *
   * @param event 鼠标事件。
   */
  function handleContextMenu(event: MouseEvent): void {
    if (!isPlacing.value) return
    event.stopPropagation()
    event.preventDefault()
    cancelPlacement()
  }

  /**
   * 绑定放置事件。
   *
   * @param dom 视口 DOM。
   */
  function bindEvents(dom: HTMLElement): void {
    dom.addEventListener('pointermove', handlePointerMove, { capture: true })
    dom.addEventListener('pointerdown', handlePointerDown, { capture: true })
    dom.addEventListener('click', handleClick, { capture: true })
    dom.addEventListener('contextmenu', handleContextMenu, { capture: true })
  }

  /**
   * 解绑放置事件。
   *
   * @param dom 视口 DOM。
   */
  function unbindEvents(dom: HTMLElement): void {
    dom.removeEventListener('pointermove', handlePointerMove, { capture: true })
    dom.removeEventListener('pointerdown', handlePointerDown, { capture: true })
    dom.removeEventListener('click', handleClick, { capture: true })
    dom.removeEventListener('contextmenu', handleContextMenu, { capture: true })
  }

  /**
   * 根据放置锚点模式刷新预览组的锚点偏移。
   *
   * @param sceneObject 待放置对象。
   * @param anchorMode 当前放置锚点模式。
   */
  function updatePreviewAnchorPoint(
    sceneObject: THREE.Object3D,
    anchorMode: ComponentPlacementAnchorMode,
  ): void {
    previewAnchorPointAtGroupOrigin.set(0, 0, 0)

    if (anchorMode !== ComponentPlacementAnchorMode.BoundingBoxCenter) {
      return
    }

    // 包围盒必须在对象已经进入预览组后计算，此时世界矩阵能反映源组件原始局部变换。
    sceneObject.updateMatrixWorld(true)
    previewBounds.makeEmpty()
    previewBounds.setFromObject(sceneObject)
    if (previewBounds.isEmpty()) {
      return
    }

    previewBounds.getCenter(previewBoundsCenter)
    previewAnchorPointAtGroupOrigin.copy(previewBoundsCenter)
  }

  /**
   * 重置一次放置流程内部的运行态标记。
   */
  function resetPlacementRuntimeFlags(): void {
    hasFocusedPreviewBounds = false
    hasLockedPlacementPlane = false
    hasReportedSnapResolveFailure = false
  }

  /**
   * 重置放置启动选项。
   */
  function resetPlacementStartOptions(): void {
    currentAnchorMode = ComponentPlacementAnchorMode.ObjectOrigin
    shouldFocusOnFirstPreviewPosition = true
    shouldResolvePlacementSnap = true
    previewAnchorPointAtGroupOrigin.set(0, 0, 0)
    confirmPosition.set(0, 0, 0)
  }

  /**
   * 启动 preview group 放置流程。
   *
   * @param sceneObject 待放置的运行时对象。
   * @param startOptions 放置流程启动参数。
   * @returns 是否成功进入放置流程。
   */
  function startPreviewObjectPlacement(
    sceneObject: THREE.Object3D,
    startOptions: ComponentPlacementStartOptions = {},
  ): boolean {
    if (isPlacing.value) {
      logger.warn('[useComponentPlacement] already in placement mode')
      return false
    }
    const scene = options.getScene()
    const dom = options.getDomElement()
    if (!scene || !dom) {
      logger.warn('[useComponentPlacement] scene or dom element not ready')
      return false
    }

    resetPlacementRuntimeFlags()
    resetPlacementStartOptions()
    sceneObject.removeFromParent()
    // 预览阶段不应进入 imported-model 的 hover/selection 高亮链，
    // 否则会被 Box3Helper 包围盒高亮污染视觉反馈。
    markPreviewObjectSkipSelection(sceneObject)

    const group = new THREE.Group()
    group.name = PREVIEW_GROUP_NAME
    options.addObject(sceneObject, group)
    options.addObject(group, scene)

    previewGroup.value = group
    sceneObjectRef = sceneObject
    existingObjectPlacementState = null
    currentDomElement = dom
    currentAnchorMode = startOptions.anchorMode ?? ComponentPlacementAnchorMode.ObjectOrigin
    shouldFocusOnFirstPreviewPosition = startOptions.focusOnFirstPreviewPosition ?? true
    shouldResolvePlacementSnap = startOptions.enableSnap ?? true
    updatePreviewAnchorPoint(sceneObject, currentAnchorMode)
    isPlacing.value = true

    if (startOptions.syncToLastPointerPositionOnStart && hasLastPointerClientPosition) {
      // Ctrl+V 刚进入放置时还没有新的 pointermove，需要用进入前缓存的视口坐标定位预览。
      updatePlacementPositionFromClientPosition(lastPointerClientPosition.x, lastPointerClientPosition.y)
    }

    bindEvents(dom)
    return true
  }

  /**
   * 启动导入模型放置预览。
   *
   * @param result 导入模型结果。
   * @returns 是否成功进入放置流程。
   */
  function startPlacement(
    result: ImportedModelResult,
    startOptions: ComponentPlacementStartOptions = {},
  ): boolean {
    return startPreviewObjectPlacement(resolveSceneObject(result), startOptions)
  }

  /**
   * 对已经加载进场景的对象启动放置流程。
   *
   * 设计说明：新插入接口会先创建提交并加载模型，之后用户仍需要像旧插入预览一样
   * 通过鼠标吸附确认最终位置；因此这里只接管事件和局部位姿，不再重复创建预览对象。
   *
   * @param object 已经挂到场景中的插入对象。
   * @returns 成功进入放置流程时返回 true。
   */
  function startExistingObjectPlacement(object: THREE.Object3D): boolean {
    if (isPlacing.value) {
      logger.warn('[useComponentPlacement] already in placement mode')
      return false
    }
    const dom = options.getDomElement()
    if (!dom) {
      logger.warn('[useComponentPlacement] dom element not ready')
      return false
    }
    if (!object.parent) {
      logger.warn('[useComponentPlacement] placement object has no parent')
      return false
    }

    resetPlacementRuntimeFlags()
    resetPlacementStartOptions()
    object.updateMatrixWorld(true)
    markPreviewObjectSkipSelection(object)
    existingObjectPlacementState = {
      object,
      parent: object.parent,
      position: object.position.clone(),
      quaternion: object.quaternion.clone(),
      scale: object.scale.clone(),
    }
    previewGroup.value = null
    sceneObjectRef = null
    currentDomElement = dom
    isPlacing.value = true

    bindEvents(dom)
    return true
  }

  /**
   * 启动 Object3D 放置。
   *
   * 设计说明：
   * 1. 传入启动选项或对象尚未挂场景时，走 preview group，用于导入模型与引用复制；
   * 2. 不传启动选项且对象已在场景中时，直接驱动该对象，用于新插入接口的已加载对象吸附。
   *
   * @param object 待放置对象。
   * @param startOptions 放置流程启动参数。
   * @returns 是否成功进入放置流程。
   */
  function startObjectPlacement(
    object: THREE.Object3D,
    startOptions: ComponentPlacementStartOptions = {},
  ): boolean {
    const shouldUsePreviewGroup = !object.parent
      || startOptions.anchorMode !== undefined
      || startOptions.focusOnFirstPreviewPosition !== undefined
      || startOptions.syncToLastPointerPositionOnStart === true
    return shouldUsePreviewGroup
      ? startPreviewObjectPlacement(object, startOptions)
      : startExistingObjectPlacement(object)
  }

  /**
   * 恢复已加载对象进入放置前的局部位姿。
   *
   * @param state 已加载对象放置状态。
   */
  function restoreExistingObjectPlacementState(state: ExistingObjectPlacementState): void {
    if (state.parent && state.object.parent !== state.parent) {
      state.parent.add(state.object)
    }
    state.object.position.copy(state.position)
    state.object.quaternion.copy(state.quaternion)
    state.object.scale.copy(state.scale)
    state.object.updateMatrixWorld(true)
  }

  /**
   * 清理放置流程运行态。
   *
   * @param teardownOptions 清理选项。
   */
  function teardown(teardownOptions: PlacementTeardownOptions = {}): void {
    options.clearPlacementSnap?.()
    if (currentDomElement) {
      unbindEvents(currentDomElement)
      currentDomElement = null
    }
    const group = previewGroup.value
    const sceneObject = sceneObjectRef
    const existingState = existingObjectPlacementState

    if (sceneObject) {
      restorePreviewObjectSkipSelection(sceneObject)
    }
    if (existingState) {
      if (teardownOptions.restoreExistingObject === true) {
        restoreExistingObjectPlacementState(existingState)
      }
      restorePreviewObjectSkipSelection(existingState.object)
    }
    if (group) {
      group.removeFromParent()
    }
    previewGroup.value = null
    sceneObjectRef = null
    existingObjectPlacementState = null
    isPlacing.value = false
    resetPlacementRuntimeFlags()
    resetPlacementStartOptions()
  }

  /**
   * 取消当前放置流程。
   */
  function cancelPlacement(): void {
    if (!isPlacing.value) return
    teardown({ restoreExistingObject: true })
    options.onCancel?.()
  }

  /**
   * 确认放置时调用：从预览 group 中取出 3D 对象，恢复原始材质，
   * 清理预览 group，返回对象供外部 reparent。
   *
   * @returns 预览对象；没有有效预览对象时返回 null。
   */
  function detachPreviewObject(): THREE.Object3D | null {
    if (!isPlacing.value) return null
    const sceneObject = sceneObjectRef
    if (!sceneObject) {
      teardown()
      return null
    }
    sceneObject.removeFromParent()
    restorePreviewObjectSkipSelection(sceneObject)
    sceneObjectRef = null
    teardown()
    return sceneObject
  }

  /**
   * 确认已加载对象放置。
   *
   * @returns 被放置的对象；当前不是已加载对象放置流程时返回 null。
   */
  function commitObjectPlacement(): THREE.Object3D | null {
    if (!isPlacing.value || !existingObjectPlacementState) return null
    const object = existingObjectPlacementState.object
    teardown({ restoreExistingObject: false })
    return object
  }

  return {
    isPlacing,
    previewGroup,
    startPlacement,
    startObjectPlacement,
    rememberPointerPosition,
    cancelPlacement,
    detachPreviewObject,
    commitObjectPlacement,
  }
}
